import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/lib/auth";
import { api, API, fmtNGN, formatErr } from "@/lib/api";
import { ReviewStatusBadge, EscalatedBadge, fmtWhen } from "@/components/ReviewBits";
import { ArrowLeft, CheckCircle, XCircle, CircleNotch, Warning, FileText, IdentificationCard, User, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import MonoLookupPanel from "@/components/MonoLookupPanel";

const CHOICES = [["yes", "Yes"], ["no", "No"], ["na", "N/A"]];
const err = (e) => toast.error(formatErr(e?.response?.data?.detail) || e.message);

export default function ReviewDetail() {
  const { kind, id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const back = user?.role === "admin" ? "/admin/reviewers" : "/approver/queue";

  const load = async () => {
    try {
      const { data: d } = await api.get(`/reviews/${kind}/${id}`);
      setData(d);
      setItems(d.checklist);
    } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, [kind, id]); // eslint-disable-line

  if (!data) return <DashboardShell title="Application review"><CircleNotch size={28} className="animate-spin text-[#0A3622]" /></DashboardShell>;
  const it = data.item;

  return (
    <DashboardShell title={`${it.loan_type} · ${it.ref}`} subtitle="Work through every checklist item, then approve or decline.">
      <Link to={back} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A3622] mb-5" data-testid="review-back-link">
        <ArrowLeft size={16} weight="bold" /> Back to queue
      </Link>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <ReviewStatusBadge status={it.review_status} testid="review-detail-status" />
        {it.escalated && <EscalatedBadge testid="review-detail-escalated" />}
        <span className="text-xs text-[#4F5D4F]" data-testid="review-detail-reviewer">Assigned to {it.reviewer_name || "—"} · {fmtWhen(it.assigned_at)}</span>
      </div>
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <ApplicantCard it={it} />
          <LoanCard kind={kind} app={data.application} it={it} />
          <KycCard kyc={data.kyc} app={data.application} kind={kind} hasInvoice={data.has_invoice} />
          <MonoLookupPanel />
        </div>
        <div className="lg:col-span-3 space-y-5">
          <Checklist kind={kind} id={id} items={items} setItems={setItems} canAct={data.can_act} />
          {data.can_act ? <DecisionPanel kind={kind} id={id} items={items} app={data.application} onDone={load} />
            : <DecidedPanel it={it} />}
        </div>
      </div>
    </DashboardShell>
  );
}

function Card({ icon, title, children, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4 text-[#0A3622]">{icon}<h2 className="font-display text-lg font-medium">{title}</h2></div>
      {children}
    </div>
  );
}

function Rows({ rows }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
      {rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
        <div key={k}><dt className="text-[11px] uppercase tracking-[0.12em] text-[#4F5D4F]">{k}</dt><dd className="font-medium text-[#132A13] mt-0.5 break-words">{v}</dd></div>
      ))}
    </dl>
  );
}

function ApplicantCard({ it }) {
  return (
    <Card icon={<User size={18} weight="fill" />} title="Applicant" testid="review-applicant-card">
      <Rows rows={[["Name", it.applicant.name], ["Email", it.applicant.email], ["Phone", it.applicant.phone], ["Applied", fmtWhen(it.applied_at)]]} />
    </Card>
  );
}

function LoanCard({ kind, app, it }) {
  const t = app.accepted_terms || {};
  const rows = kind === "school_fee"
    ? [["Loan type", it.loan_type], ["Applied amount", fmtNGN(app.requested_amount || app.amount)], ["Offered (AIP)", fmtNGN(app.amount)], ["Tenor", `${app.tenor_months} months`],
       ["Avg net monthly income", fmtNGN(app.avg_net_monthly_income ?? app.monthly_income)], ["Proposed monthly repayment", fmtNGN(app.monthly_payment)],
       ["Repayment-to-income", app.rti_pct != null ? `${app.rti_pct}% (cap 33%)` : null], ["Tenor stretched from", app.tenor_extended_from && `${app.tenor_extended_from} months`],
       ["Existing monthly debt", fmtNGN(app.existing_monthly_debt)], ["School", app.school_name || app.payee?.school_name], ["Child", app.remark],
       ["Risk band", app.ai_risk_band], ["Rate", app.monthly_rate_pct && `${app.monthly_rate_pct}% / month`], ["Repayment method", app.repayment_method]]
    : [["Loan type", it.loan_type], ["Applied amount", fmtNGN(t.principal)], ["Tenor", t.tenor_days && `${t.tenor_days} days`], ["Instalment", fmtNGN(t.monthly_installment)],
       ["Total repayment", fmtNGN(t.total_repayment)], ["Institution", app.kyc?.institution], ["Mandate", app.mandate_reference]];
  return <Card icon={<HandCoins size={18} weight="fill" />} title="Loan" testid="review-loan-card"><Rows rows={rows} /></Card>;
}

function KycCard({ kyc, app, kind, hasInvoice }) {
  const rows = [["BVN", kyc.bvn_last4 && `****${kyc.bvn_last4}`], ["NIN", kyc.nin_last4 && `****${kyc.nin_last4}`], ["BVN–NIN match", kyc.bvn_nin_match != null ? `${kyc.bvn_nin_match}%` : null],
    ["Name on BVN", kyc.bvn_name], ["DOB on BVN", kyc.bvn_dob], ["Checked by", kyc.provider], ["KYC record", kyc.record_status], ["Name submitted", kyc.name_submitted]];
  if (kind === "ala") rows.push(["Staff ID", app.kyc?.staff_id], ["Official email", app.kyc?.official_email], ["Email verified", fmtWhen(app.kyc?.official_email_verified_at)]);
  return (
    <Card icon={<IdentificationCard size={18} weight="fill" />} title="KYC documents" testid="review-kyc-card">
      <Rows rows={rows} />
      {kyc.record_reason && <p className="text-xs text-[#4F5D4F] mt-3">{kyc.record_reason}</p>}
      {hasInvoice && (
        <a href={`${API}/admin/loans/${app.id}/invoice`} target="_blank" rel="noreferrer" className="btn-outline text-sm mt-4 inline-flex" data-testid="review-open-invoice">
          <FileText size={14} weight="fill" /> Open school invoice
        </a>
      )}
    </Card>
  );
}

function Checklist({ kind, id, items, setItems, canAct }) {
  const done = items.filter((i) => i.value).length;
  const save = async (key, patch) => {
    const cur = items.find((i) => i.key === key);
    const next = { ...cur, ...patch };
    setItems((xs) => xs.map((x) => (x.key === key ? next : x)));
    if (!next.value) return;
    try { await api.put(`/reviews/${kind}/${id}/checklist`, { items: { [key]: { value: next.value, note: next.note || null } } }); }
    catch (e) { err(e); }
  };
  return (
    <Card icon={<CheckCircle size={18} weight="fill" />} title="Reviewer checklist" testid="review-checklist">
      <div className="flex items-center justify-between text-xs text-[#4F5D4F] mb-2">
        <span>Mark every item before deciding. Anything marked No needs a note.</span>
        <span className="font-mono" data-testid="review-checklist-progress">{done}/{items.length}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#E5E3DB] overflow-hidden mb-5"><div className="h-full bg-[#0A3622] transition-all" style={{ width: `${(100 * done) / items.length}%` }} /></div>
      <ol className="space-y-3">
        {items.map((i, n) => <ChecklistRow key={i.key} n={n + 1} item={i} canAct={canAct} save={save} />)}
      </ol>
    </Card>
  );
}

function ChecklistRow({ n, item, canAct, save }) {
  const [note, setNote] = useState(item.note || "");
  const flagged = item.value === "no";
  useEffect(() => {
    if (note === (item.note || "")) return undefined;
    const t = setTimeout(() => save(item.key, { note }), 500);
    return () => clearTimeout(t);
  }, [note]); // eslint-disable-line
  return (
    <li className={`rounded-xl border p-4 ${flagged ? "border-[#C93B3B]/50 bg-[#FBE3E3]/30" : "border-[#E5E3DB]"}`} data-testid={`checklist-item-${item.key}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#132A13]">{n}. {item.label}</div>
          {item.hint && <div className="text-xs text-[#4F5D4F] mt-1" data-testid={`checklist-hint-${item.key}`}>{item.hint}</div>}
        </div>
        <div className="flex shrink-0 rounded-full border border-[#E5E3DB] overflow-hidden" role="radiogroup">
          {CHOICES.map(([v, label]) => (
            <button key={v} type="button" disabled={!canAct} onClick={() => save(item.key, { value: v })}
              className={`px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed ${item.value === v ? (v === "no" ? "bg-[#C93B3B] text-white" : "bg-[#0A3622] text-white") : "bg-white text-[#4F5D4F] hover:bg-[#F0EFEA]"}`}
              data-testid={`checklist-${item.key}-${v}`}>{label}</button>
          ))}
        </div>
      </div>
      {flagged && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#C93B3B] mb-1"><Warning size={14} weight="fill" /> Exception flagged — explain why</div>
          <textarea className="input text-sm" rows={2} value={note} disabled={!canAct} onChange={(e) => setNote(e.target.value)}
            placeholder="What's the exception and why is it acceptable (or not)?"
            data-testid={`checklist-note-${item.key}`} />
        </div>
      )}
    </li>
  );
}

function DecisionPanel({ kind, id, items, app, onDone }) {
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState(app.amount || 0);
  const [busy, setBusy] = useState(false);
  const left = items.filter((i) => !i.value).length;
  const missingNotes = items.filter((i) => i.value === "no" && (i.note || "").trim().length < 3).length;
  const ready = left === 0 && missingNotes === 0;

  const decide = async (decision) => {
    if (decision === "decline" && reason.trim().length < 5) return toast.error("Add a reason for declining (at least 5 characters)");
    setBusy(true);
    try {
      const all = Object.fromEntries(items.map((i) => [i.key, { value: i.value, note: i.note || null }]));
      await api.put(`/reviews/${kind}/${id}/checklist`, { items: all });
      await api.post(`/reviews/${kind}/${id}/decision`, { decision, reason: reason.trim() || null, final_amount: kind === "school_fee" ? Number(amount) : null });
      toast.success(decision === "approve" ? "Application approved" : "Application declined — applicant notified");
      onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <Card icon={<CheckCircle size={18} weight="fill" />} title="Decision" testid="review-decision-panel">
      {!ready && (
        <p className="text-xs text-[#B45309] mb-4" data-testid="review-decision-blocked">
          {left > 0 ? `${left} checklist item(s) still to mark. ` : ""}{missingNotes > 0 ? `${missingNotes} item(s) marked No need a note.` : ""}
        </p>
      )}
      {kind === "school_fee" && (
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Final loan amount (₦) — sent in the offer letter</span>
          <input type="number" min="1" className="input mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="review-final-amount" />
        </label>
      )}
      <label className="block mb-5">
        <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Reason <span className="normal-case tracking-normal font-normal">(required to decline, optional to approve)</span></span>
        <textarea className="input mt-1" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-reason-input" />
      </label>
      <div className="flex flex-wrap gap-3">
        <button onClick={() => decide("approve")} disabled={!ready || busy || (kind === "school_fee" && !(Number(amount) > 0))} className="btn-primary disabled:opacity-50" data-testid="review-approve-button">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <><CheckCircle size={16} weight="fill" /> Approve</>}
        </button>
        <button onClick={() => decide("decline")} disabled={!ready || busy || reason.trim().length < 5} className="btn-outline disabled:opacity-50" data-testid="review-decline-button">
          <XCircle size={16} weight="fill" /> Decline
        </button>
      </div>
    </Card>
  );
}

function DecidedPanel({ it }) {
  return (
    <Card icon={<CheckCircle size={18} weight="fill" />} title="Decision" testid="review-decided-panel">
      {["approved", "declined"].includes(it.review_status) ? (
        <p className="text-sm text-[#4F5D4F]"><ReviewStatusBadge status={it.review_status} /> on {fmtWhen(it.decided_at)}{it.decision_reason ? ` — ${it.decision_reason}` : ""}</p>
      ) : <p className="text-sm text-[#4F5D4F]">You can view this application but not act on it.</p>}
    </Card>
  );
}
